"use client";

import { c as _c } from "react/compiler-runtime";
import { QueueItem, QueueItemAction, QueueItemActions, QueueItemContent, QueueItemFile } from "@workspace/ui/components/ai-elements/queue";
import { cn } from "@workspace/ui/lib/utils";
import { GripVerticalIcon, PencilIcon, XIcon } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useRef, useState } from "react";
import { moveFollowUp, moveFollowUpTo, queuedFollowUpLabel, queuedFollowUpUploadIds, removeFollowUp, useFollowUpQueue } from "./message-queue-store";
import type { EditorFilePart } from "./serialize";

// Queue actions fade in on row hover by default. These stay put: edit and
// remove are the only way to manage a queued row, and hover doesn't exist on
// the touch surfaces this ships to.
const ALWAYS_VISIBLE_ACTION = "opacity-100";
export function FollowUpQueue(t0) {
  const $ = _c(17);
  const {
    chatId,
    onDeleteUploads,
    onEdit
  } = t0;
  const items = useFollowUpQueue(chatId);
  const listRef = useRef(null);
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  if (items.length === 0) {
    return null;
  }
  const dropIndexFromY = clientY => {
    const rows = listRef.current?.querySelectorAll(":scope > li");
    if (!rows || rows.length === 0) {
      return items.length;
    }
    for (let index = 0; index < rows.length; index++) {
      const rect = rows[index]?.getBoundingClientRect();
      if (rect && clientY < rect.top + rect.height / 2) {
        return index;
      }
    }
    return items.length;
  };
  const endDrag = clientY_0 => {
    const drag = dragRef.current;
    dragRef.current = null;
    const dest = clientY_0 === undefined ? dropIndex : dropIndexFromY(clientY_0);
    if (drag && dest !== null) {
      moveFollowUpTo(chatId, drag.id, dest);
    }
    setDraggingId(null);
    setDropIndex(null);
  };
  const t1 = "mx-auto mb-2 min-w-0 w-full max-w-3xl px-2 @[50rem]:px-0";
  let t2;
  if ($[0] !== items.length) {
    t2 = items.length > 3 ? <p className="px-3 py-1 text-muted-foreground text-xs">{items.length} queued</p> : null;
    $[0] = items.length;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = items.length > 3 && "max-h-28 overflow-y-auto md:max-h-40";
  const t4 = draggingId && "select-none";
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = cn("flex flex-col gap-0.5", t3, t4);
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = items.map((item, index_0) => {
    const label = queuedFollowUpLabel(item.parts);
    const files = item.parts.filter(_temp);
    return <QueueItem className={cn("relative flex-row items-center gap-0 rounded-lg py-1.5", draggingId === item.id && "opacity-40")} key={item.id}>{draggingId && dropIndex === index_0 ? <div className="absolute inset-x-3 top-0 z-10 h-px bg-foreground/50" /> : null}{items.length > 1 ? <button aria-label="Reorder follow-up" className="mr-1 shrink-0 cursor-grab touch-none rounded p-0.5 text-muted-foreground hover:text-foreground active:cursor-grabbing" onKeyDown={event => {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          moveFollowUp(chatId, item.id, "up");
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          moveFollowUp(chatId, item.id, "down");
        }
      }} onPointerCancel={() => endDrag()} onPointerDown={event_0 => {
        if (event_0.button !== 0) {
          return;
        }
        event_0.preventDefault();
        event_0.currentTarget.setPointerCapture(event_0.pointerId);
        dragRef.current = {
          id: item.id
        };
        setDraggingId(item.id);
        setDropIndex(index_0);
      }} onPointerMove={event_1 => {
        if (!dragRef.current) {
          return;
        }
        setDropIndex(dropIndexFromY(event_1.clientY));
      }} onPointerUp={event_2 => {
        endDrag(event_2.clientY);
      }} type="button"><GripVerticalIcon className="size-3.5" /></button> : null}{index_0 === 0 ? <span className="mr-1.5 shrink-0 text-muted-foreground text-xs leading-none">Next</span> : null}<QueueItemContent className="min-w-0">{label}</QueueItemContent>{files.length > 0 ? <span className="ml-2 flex min-w-0 shrink-0 gap-1">{files.map(_temp2)}</span> : null}<QueueItemActions className="ml-auto shrink-0"><QueueItemAction aria-label="Edit follow-up" className={ALWAYS_VISIBLE_ACTION} onClick={() => onEdit?.(item.id)}><PencilIcon className="size-3.5" /></QueueItemAction><QueueItemAction aria-label="Remove follow-up" className={ALWAYS_VISIBLE_ACTION} onClick={() => {
          const removed = removeFollowUp(chatId, item.id);
          if (removed) {
            const uploadIds = queuedFollowUpUploadIds(removed.parts);
            if (uploadIds.length > 0) {
              onDeleteUploads?.(uploadIds);
            }
          }
        }}><XIcon className="size-3.5" /></QueueItemAction></QueueItemActions></QueueItem>;
  });
  let t7;
  if ($[5] !== listRef || $[6] !== t5 || $[7] !== t6) {
    t7 = <ul className={t5} ref={listRef}>{t6}</ul>;
    $[5] = listRef;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== draggingId || $[10] !== dropIndex || $[11] !== items.length) {
    t8 = draggingId && dropIndex === items.length ? <div className="mx-3 h-px bg-foreground/50" /> : null;
    $[9] = draggingId;
    $[10] = dropIndex;
    $[11] = items.length;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t7 || $[15] !== t8) {
    t9 = <div className={t1}>{t2}{t7}{t8}</div>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function _temp2(file) {
  return <QueueItemFile key={file.data?.uploadId ?? file.url}>{file.filename}</QueueItemFile>;
}
function _temp(part) {
  return part.type === "file";
}